import { createUseStyles } from 'react-jss';
import { BackgroundColors } from '@picsart/design-system/foundation/backgroundColors';
import { TextColors } from '@picsart/design-system/foundation/textColors';
import { ErrorColors } from '@picsart/design-system/foundation/errorColors';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import {
  FontSizes,
  LineHeights,
} from '@picsart/design-system/foundation/typography';
import { FontWeights } from '@picsart/design-system/foundation/fonts';

const GILROY = '"Picsart-Fonts", sans-serif';

const useStyles = createUseStyles(
  {
    page: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      backgroundColor: BackgroundColors.tint1,
    },
    pageHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s4,
      padding: `0 ${Spacings.s4}`,
      backgroundColor: BackgroundColors.base,
      borderTop: `1px solid ${BackgroundColors.border}`,
      borderBottom: `1px solid ${BackgroundColors.border}`,
      flexShrink: 0,
    },
    pageTitle: {
      fontFamily: GILROY,
      fontSize: FontSizes.s16,
      fontWeight: FontWeights.semibold,
      lineHeight: LineHeights.l24,
      color: TextColors.base,
    },
    dropdown: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: 'calc(100% + 6px)',
      right: 0,
      backgroundColor: BackgroundColors.tint1,
      border: `1px solid ${BackgroundColors.border}`,
      borderRadius: CornerRadius.r12,
      zIndex: 500,
      width: 360,
      maxHeight: 620,
      overflowY: 'auto',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    },
    dropdownHeader: {
      padding: `${Spacings.s12} ${Spacings.s16} ${Spacings.s4}`,
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: `${Spacings.s8} ${Spacings.s12}`,
    },
    section: {
      marginBottom: Spacings.s16,
    },
    sectionTitle: {
      fontFamily: GILROY,
      fontSize: FontSizes.s12,
      fontWeight: FontWeights.semibold,
      lineHeight: LineHeights.l16,
      color: TextColors.tint2,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      padding: `0 ${Spacings.s12} ${Spacings.s8}`,
      display: 'block',
    },
    sectionItems: {
      display: 'flex',
      flexDirection: 'column',
      borderRadius: CornerRadius.r12,
      overflow: 'hidden',
      backgroundColor: BackgroundColors.base,
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s12,
      width: '100%',
      padding: `${Spacings.s12} ${Spacings.s12}`,
      border: 'none',
      backgroundColor: BackgroundColors.base,
      color: TextColors.base,
      fontFamily: GILROY,
      fontSize: FontSizes.s16,
      fontWeight: FontWeights.medium,
      lineHeight: LineHeights.l24,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      WebkitTapHighlightColor: 'transparent',
      '& + &': {
        borderTop: `1px solid ${BackgroundColors.tint2}`,
      },
      '&:active': {
        backgroundColor: BackgroundColors.tint1,
      },
    },
    menuItemIcon: {
      width: 20,
      height: 20,
      flexShrink: 0,
      color: TextColors.tint1,
    },
    menuItemLabel: {
      flex: 1,
      textAlign: 'left',
    },
    menuItemChevron: {
      width: 16,
      height: 16,
      flexShrink: 0,
      color: TextColors.tint2,
    },
    versionRow: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s12,
      padding: `${Spacings.s4} ${Spacings.s12} ${Spacings.s8}`,
    },
    versionIcon: {
      width: 16,
      height: 16,
      flexShrink: 0,
      color: TextColors.tint2,
    },
    versionLabel: {
      fontFamily: GILROY,
      fontSize: FontSizes.s14,
      color: TextColors.tint2,
      flex: 1,
    },
    versionValue: {
      fontFamily: GILROY,
      fontSize: FontSizes.s14,
      color: TextColors.tint2,
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      padding: `0 ${Spacings.s12}`,
      marginTop: 'auto',
      marginBottom: Spacings.s16,
      flexShrink: 0,
      '& > *': {
        borderRadius: 0,
      },
      '& > :first-child': {
        borderTopLeftRadius: CornerRadius.r12,
        borderTopRightRadius: CornerRadius.r12,
      },
      '& > :last-child': {
        borderBottomLeftRadius: CornerRadius.r12,
        borderBottomRightRadius: CornerRadius.r12,
      },
      '& > * + *': {
        borderTop: `1px solid ${BackgroundColors.tint2}`,
      },
    },
    dangerItem: {
      display: 'flex',
      alignItems: 'center',
      gap: Spacings.s12,
      width: '100%',
      padding: `${Spacings.s12} ${Spacings.s12}`,
      border: 'none',
      backgroundColor: BackgroundColors.base,
      color: ErrorColors.base.default,
      fontFamily: GILROY,
      fontSize: FontSizes.s16,
      fontWeight: FontWeights.medium,
      lineHeight: LineHeights.l24,
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      WebkitTapHighlightColor: 'transparent',
      '&:active': {
        backgroundColor: BackgroundColors.tint1,
      },
    },
    dangerIcon: {
      width: 20,
      height: 20,
      flexShrink: 0,
    },
  },
  { name: 'Settings' }
);

export default useStyles;
